// Cervical Spine (Neck) Template Configuration
// Questions simplified to 12-year-old reading level

export const cervicalTemplate = {
  id: 'CERVICAL_SPINE',
  name: 'Neck',
  icon: '🦴',
  dominantSide: 'hand',

  fields: {
    problemSide: {
      type: 'radio',
      label: 'Which side of your neck is affected?',
      options: ['Right side', 'Left side', 'Middle/both sides'],
      required: true
    },

    problemDate: {
      type: 'date',
      label: 'When did your neck problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Neck pain',
        'Stiffness',
        'Headaches',
        'Pain going down my arm',
        'Tingling or numbness in my arm/hand',
        'Weakness in my arm/hand',
        'Dizziness',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Other problem (please describe):',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    problemDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your neck problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly do you feel pain?',
      options: [
        'Back of neck',
        'Side of neck',
        'Top of neck (near head)',
        'Shoulder',
        'Between shoulder blades',
        'Down my arm'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Aching', 'Sharp', 'Burning', 'Throbbing', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'All the time',
        'In the morning',
        'At night',
        'When I move my neck',
        'After sitting for a long time'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Turning my head',
        'Looking up',
        'Looking down',
        'Sitting at computer',
        'Lifting things',
        'Driving',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Heat',
        'Pain medication',
        'Lying down',
        'Wearing a neck collar',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Arm Pain Section
    armPain: {
      type: 'radio',
      label: 'Does the pain go down your arm?',
      options: ['Yes', 'No']
    },

    armPainLocation: {
      type: 'checkbox-multiple',
      label: 'Where does the pain go in your arm?',
      options: ['Shoulder', 'Upper arm', 'Forearm', 'Hand', 'Fingers'],
      showWhen: { field: 'armPain', equals: 'Yes' }
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Is your neck stiff?',
      options: ['Yes', 'No']
    },

    stiffnessMovements: {
      type: 'checkbox-multiple',
      label: 'Which movements are stiff?',
      options: [
        'Looking up',
        'Looking down',
        'Turning head right',
        'Turning head left',
        'Tilting head sideways'
      ],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    morningStiffness: {
      type: 'radio',
      label: 'Is your neck worse first thing in the morning?',
      options: ['Yes', 'No']
    },

    morningStiffnessDuration: {
      type: 'radio',
      label: 'How long does the morning stiffness last?',
      options: ['Less than 30 minutes', '30-60 minutes', 'More than 1 hour'],
      showWhen: { field: 'morningStiffness', equals: 'Yes' }
    },

    // Neurological Symptoms (Red Flags)
    hasNumbness: {
      type: 'radio',
      label: 'Do you have any numbness or tingling in your arms or hands?',
      options: ['Yes', 'No']
    },

    numbnessLocation: {
      type: 'text',
      label: 'Where do you feel the numbness or tingling?',
      placeholder: 'e.g., thumb and first two fingers, outside of hand',
      showWhen: { field: 'hasNumbness', equals: 'Yes' }
    },

    hasWeakness: {
      type: 'radio',
      label: 'Do you have any weakness in your arms or hands?',
      options: ['Yes', 'No']
    },

    weaknessDescription: {
      type: 'text',
      label: 'What kind of weakness do you notice?',
      placeholder: 'e.g., dropping things, trouble gripping',
      showWhen: { field: 'hasWeakness', equals: 'Yes' }
    },

    balanceProblems: {
      type: 'radio',
      label: 'Have you noticed any problems with your balance or walking?',
      options: ['Yes', 'No']
    },

    bladderBowelProblems: {
      type: 'radio',
      label: 'Have you had any new problems with your bladder or bowels?',
      options: ['Yes', 'No']
    },

    electricShockFeeling: {
      type: 'radio',
      label: 'Do you get an electric shock feeling down your spine when you bend your head forward?',
      options: ['Yes', 'No']
    },

    // Functional Limitations
    drivingDifficulty: {
      type: 'radio',
      label: 'Do you have trouble turning your head when driving?',
      options: ['Yes', 'No', 'I don\'t drive']
    },

    computerWork: {
      type: 'radio',
      label: 'Does using a computer make your neck worse?',
      options: ['Yes', 'No', 'I don\'t use computers much']
    },

    sleepingDifficulty: {
      type: 'radio',
      label: 'Does your neck affect your sleep?',
      options: ['Yes', 'No']
    },

    dailyActivities: {
      type: 'checkbox-multiple',
      label: 'What activities are difficult because of your neck?',
      options: [
        'Looking up/down',
        'Lifting things',
        'Working at computer',
        'Driving',
        'Dressing myself',
        'None - I can do everything okay'
      ]
    },

    // Previous History
    previousNeckProblems: {
      type: 'radio',
      label: 'Have you had neck problems before?',
      options: ['Yes', 'No']
    },

    previousNeckDetails: {
      type: 'textarea',
      label: 'Tell us about your previous neck problems:',
      rows: 2,
      showWhen: { field: 'previousNeckProblems', equals: 'Yes' }
    },

    whiplashHistory: {
      type: 'radio',
      label: 'Have you ever had whiplash (like from a car accident)?',
      options: ['Yes', 'No']
    },

    neckSurgery: {
      type: 'radio',
      label: 'Have you ever had neck surgery?',
      options: ['Yes', 'No']
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Rheumatoid arthritis',
        'Ankylosing spondylitis (spinal arthritis)',
        'Osteoporosis',
        'Cancer',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take regularly?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies to medications or other things'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    workPosture: {
      type: 'radio',
      label: 'Does your job involve sitting at a desk or computer?',
      options: ['Yes, most of the day', 'Yes, some of the time', 'No']
    },

    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., swimming, cycling, gym'
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    },

    stress: {
      type: 'radio',
      label: 'Are you under a lot of stress at the moment?',
      options: ['Yes', 'No', 'A bit']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'problem',
      title: '2. About Your Neck Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['problemDate', 'problemSide', 'onsetType', 'mainProblem', 'mainProblemOther', 'problemDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'armSymptoms',
      title: '4. Arm & Hand Symptoms',
      component: 'GenericSection',
      enabled: true,
      fields: ['armPain', 'armPainLocation', 'hasNumbness', 'numbnessLocation', 'hasWeakness', 'weaknessDescription']
    },
    {
      id: 'stiffness',
      title: '5. Stiffness',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessMovements', 'morningStiffness', 'morningStiffnessDuration']
    },
    {
      id: 'redFlags',
      title: '6. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        { id: 'balanceProblems', label: 'Problems with balance or walking', weight: 8, urgent: true },
        { id: 'bladderBowelProblems', label: 'New bladder or bowel problems', weight: 10, urgent: true },
        { id: 'electricShockFeeling', label: 'Electric shock feeling down spine', weight: 6 }
      ],
      alerts: {
        critical: { threshold: 10, message: 'URGENT: These symptoms need immediate medical attention' },
        urgent: { threshold: 6, message: 'Please contact us promptly about these symptoms' }
      }
    },
    {
      id: 'functional',
      title: '7. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['drivingDifficulty', 'computerWork', 'sleepingDifficulty', 'dailyActivities']
    },
    {
      id: 'history',
      title: '8. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousNeckProblems', 'previousNeckDetails', 'whiplashHistory', 'neckSurgery']
    },
    {
      id: 'medical',
      title: '9. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Cervical disc herniation',
      'Cervical spondylosis',
      'Cervical radiculopathy',
      'Cervical facet joint pain',
      'Mechanical neck pain',
      'Whiplash associated disorder',
      'Cervical myelopathy'
    ]
  }
};

export default cervicalTemplate;
